/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import { downloadCsvFile, generateFileName } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
      isFetchingInboxConversationMetric: false,
      isFetchingDepartmentConversationMetric: false,
      isFetchingAgentOnlineHeatmap: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
    agentConversationMetricTotalCount: 0,
    inboxConversationMetric: [],
    inboxConversationMetricTotalCount: 0,
    departmentConversationMetric: [],
    departmentConversationMetricTotalCount: 0,
    agentOnlineHeatmap: [],
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getAgentConversationMetricTotalCount(_state) {
    return _state.overview.agentConversationMetricTotalCount;
  },
  getInboxConversationMetric(_state) {
    return _state.overview.inboxConversationMetric;
  },
  getInboxConversationMetricTotalCount(_state) {
    return _state.overview.inboxConversationMetricTotalCount;
  },
  getDepartmentConversationMetric(_state) {
    return _state.overview.departmentConversationMetric;
  },
  getDepartmentConversationMetricTotalCount(_state) {
    return _state.overview.departmentConversationMetricTotalCount;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getAgentOnlineHeatmapData(_state) {
    return _state.overview.agentOnlineHeatmap;
  },
};

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);
    Report.getReports({ ...reportObj, groupBy: 'hour' }).then(heatmapData => {
      let { data } = heatmapData;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(
        data,
        state.overview.accountConversationHeatmap
      );

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours,
      reportObj.inbox
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    return Report.getConversationMetric(reportObj.type)
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.apiGetAgentConversationMetric(reportObj)
      .then(agentConversationMetric => {
        if (agentConversationMetric.ret !== 200) {
          return Promise.reject();
        }
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric?.data?.dataList ?? [],
        );
        commit(types.default.SET_AGENT_CONVERSATION_METRIC_TOTAL_COUNT, agentConversationMetric?.data?.totalCount ?? 0);
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchInboxConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_INBOX_CONVERSATION_METRIC_LOADING, true);
    Report.apiGetInboxConversationMetric(reportObj)
      .then(inboxConversationMetric => {
        if (inboxConversationMetric.ret !== 200) {
          return Promise.reject();
        }
        commit(
          types.default.SET_INBOX_CONVERSATION_METRIC,
          inboxConversationMetric?.data?.dataList ?? [],
        );
        commit(types.default.SET_INBOX_CONVERSATION_METRIC_TOTAL_COUNT, inboxConversationMetric?.data?.totalCount ?? 0);
        commit(types.default.TOGGLE_INBOX_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_INBOX_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchDepartmentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_DEPARTMENT_CONVERSATION_METRIC_LOADING, true);
    Report.apiGetDepartmentConversationMetric(reportObj)
      .then(departmentConversationMetric => {
        if (departmentConversationMetric.ret !== 200) {
          return Promise.reject();
        }
        commit(
          types.default.SET_DEPARTMENT_CONVERSATION_METRIC,
          departmentConversationMetric?.data ?? [],
        );
        commit(types.default.SET_DEPARTMENT_CONVERSATION_METRIC_TOTAL_COUNT, departmentConversationMetric?.data?.length ?? 0);
        commit(types.default.TOGGLE_DEPARTMENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_DEPARTMENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  downloadAgentReports(_, reportObj) {
    return Report.getAgentReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAgentDailyReports(_, reportObj) {
    return Report.getAgentDailyReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent_daily',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    return Report.getLabelReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'label',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadInboxReports(_, reportObj) {
    return Report.getInboxReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'inbox',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.getTeamReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'team',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV()
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  fetchAgentOnlineHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_ONLINE_HEATMAP_LOADING, true);
    Report.getAgentOnlineHeatmap(reportObj)
      .then(agentOnlineHeatmap => {
        commit(types.default.SET_AGENT_ONLINE_HEATMAP, agentOnlineHeatmap.data?.data);
        commit(types.default.TOGGLE_AGENT_ONLINE_HEATMAP_LOADING, false);
      })
      .catch(error => {
        console.error(error);
      });
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC_TOTAL_COUNT](_state, totalCount) {
    _state.overview.agentConversationMetricTotalCount = totalCount;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_AGENT_ONLINE_HEATMAP](_state, heatmapData) {
    _state.overview.agentOnlineHeatmap = heatmapData;
  },
  [types.default.TOGGLE_AGENT_ONLINE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentOnlineHeatmap = flag;
  },
  [types.default.SET_INBOX_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.inboxConversationMetric = metricData;
  },
  [types.default.TOGGLE_INBOX_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingInboxConversationMetric = flag;
  },
  [types.default.SET_INBOX_CONVERSATION_METRIC_TOTAL_COUNT](_state, totalCount) {
    _state.overview.inboxConversationMetricTotalCount = totalCount;
  },
  [types.default.SET_DEPARTMENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.departmentConversationMetric = metricData;
  },
  [types.default.TOGGLE_DEPARTMENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingDepartmentConversationMetric = flag;
  },
  [types.default.SET_DEPARTMENT_CONVERSATION_METRIC_TOTAL_COUNT](_state, totalCount) {
    _state.overview.departmentConversationMetricTotalCount = totalCount;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
